"use client";

import type { Table } from "@tanstack/react-table";
import {
  ChevronLeft,
  ChevronRight,
  Ellipsis,
} from "lucide-react";
import { useQueryState } from "nuqs";
import * as React from "react";

import { Button } from "@ocr-monorepo/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@ocr-monorepo/ui/components/dialog";
import { Input } from "@ocr-monorepo/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ocr-monorepo/ui/components/select";
import { cn } from "@ocr-monorepo/ui/lib/utils";
import { tableQueryParams } from "@/lib/searchParams";

interface DataPaginatorProps<T> {
  table: Table<T>;
  totalItems: number;
  className?: string;
  showInfo?: boolean;
  showPageSizeSelector?: boolean;
  pageSizeOptions?: number[];
}

export function DataPaginator<T>({
  table,
  totalItems,
  className,
  showInfo = false,
  showPageSizeSelector = true,
  pageSizeOptions = [10, 20, 50, 100],
}: DataPaginatorProps<T>) {
  const [isLoading, startTransition] = React.useTransition();
  const [page, setPage] = useQueryState(
    "page",
    tableQueryParams.page.withOptions({ startTransition, shallow: false }),
  );
  const [pageSize, setPageSize] = useQueryState(
    "count",
    tableQueryParams.count.withOptions({ startTransition, shallow: false }),
  );

  const hasItems = totalItems > 0;
  const totalPages = Math.max(table.getPageCount(), 1);
  const currentPage = hasItems ? Math.min(Math.max(page, 1), totalPages) : 1;
  const itemsPerPage = table.getState().pagination.pageSize;
  const firstItem = hasItems ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const lastItem = hasItems ? Math.min(currentPage * itemsPerPage, totalItems) : 0;
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [pageInput, setPageInput] = React.useState(String(currentPage));
  const [rowInput, setRowInput] = React.useState(String(firstItem));
  const id = React.useId();

  React.useEffect(() => {
    if (pageSize !== itemsPerPage) table.setPageSize(pageSize);
  }, [pageSize, itemsPerPage, table]);

  const goToPage = (nextPage: number) => {
    setPage(Math.min(Math.max(nextPage, 1), totalPages));
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setPage(1);
  };

  const handlePageInput = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const requestedPage = Number.parseInt(pageInput, 10);
    if (!Number.isNaN(requestedPage)) {
      goToPage(requestedPage);
      setDialogOpen(false);
    }
  };

  const handleRowInput = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const requestedRow = Number.parseInt(rowInput, 10);
    if (Number.isNaN(requestedRow)) return;
    const clampedRow = Math.min(Math.max(requestedRow, 1), totalItems);
    goToPage(Math.ceil(clampedRow / itemsPerPage));
    setDialogOpen(false);
  };

  const handleDialogChange = (open: boolean) => {
    setDialogOpen(open);
    if (open) {
      setPageInput(String(currentPage));
      setRowInput(String(firstItem));
    }
  };

  return (
    <nav
      aria-label="Table pagination"
      className={cn("flex min-h-[52px] shrink-0 flex-wrap items-center justify-end gap-3 py-2", className)}
    >
      <div className="mr-auto text-xs tabular-nums text-muted-foreground">
        {showInfo ? `${totalItems.toLocaleString()} records · Scroll horizontally to see all fields` : `Page ${currentPage} of ${totalPages}`}
      </div>
      <Button variant="ghost" size="sm" onClick={() => goToPage(currentPage - 1)} disabled={isLoading || !hasItems || currentPage === 1} aria-label="Previous page">
        <ChevronLeft className="size-3.5 sm:hidden" /><span className="hidden sm:inline">Previous</span>
      </Button>
      <span className="text-xs text-muted-foreground">Page {currentPage} of {totalPages}</span>
      <Button variant="ghost" size="sm" onClick={() => goToPage(currentPage + 1)} disabled={isLoading || !hasItems || currentPage === totalPages} aria-label="Next page">
        <span className="hidden sm:inline">Next</span><ChevronRight className="size-3.5 sm:hidden" />
      </Button>

      {showPageSizeSelector ? <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" aria-label="More pagination options" disabled={!hasItems}>
            <Ellipsis className="size-3.5" /><span className="hidden sm:inline">More</span>
          </Button>
        </DialogTrigger>
        <DialogContent className="gap-0 p-0 sm:max-w-sm">
          <DialogHeader className="border-b border-border px-5 py-4">
            <DialogTitle className="text-base">Pagination options</DialogTitle>
            <DialogDescription className="text-xs">Move directly through this result set.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 px-5 py-5">
            <form className="space-y-2" onSubmit={handlePageInput}>
              <div className="flex items-baseline justify-between gap-3">
                <label htmlFor={`${id}-page`} className="text-xs font-medium">Page</label>
                <span className="font-mono text-[10px] text-muted-foreground">{currentPage} of {totalPages}</span>
              </div>
              <div className="flex gap-2">
                <Input id={`${id}-page`} value={pageInput} inputMode="numeric" pattern="[0-9]*" onChange={(event) => setPageInput(event.target.value)} className="h-8 rounded-sm font-mono text-xs tabular-nums" aria-label={`Go to page, 1 through ${totalPages}`} />
                <Button type="submit" size="sm" disabled={isLoading}>Go</Button>
              </div>
            </form>

            {showPageSizeSelector ? (
              <div className="space-y-2">
                <label htmlFor={`${id}-page-size`} className="text-xs font-medium">Rows per page</label>
                <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger id={`${id}-page-size`} className="h-8 w-full rounded-sm font-mono text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {pageSizeOptions.map((size) => <SelectItem key={size} value={String(size)} className="font-mono text-xs">{size}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <form className="space-y-2" onSubmit={handleRowInput}>
              <div className="flex items-baseline justify-between gap-3">
                <label htmlFor={`${id}-row`} className="text-xs font-medium">Go to row</label>
                <span className="font-mono text-[10px] text-muted-foreground">1–{totalItems.toLocaleString()}</span>
              </div>
              <div className="flex gap-2">
                <Input id={`${id}-row`} value={rowInput} inputMode="numeric" pattern="[0-9]*" onChange={(event) => setRowInput(event.target.value)} className="h-8 rounded-sm font-mono text-xs tabular-nums" aria-label={`Go to row, 1 through ${totalItems}`} />
                <Button type="submit" variant="outline" size="sm" disabled={isLoading}>Go</Button>
              </div>
            </form>
          </div>

          <DialogFooter className="flex-row justify-between border-t border-border px-5 py-3 sm:justify-between">
            <Button variant="ghost" size="sm" onClick={() => { goToPage(1); setDialogOpen(false); }} disabled={isLoading || currentPage === 1}>First page</Button>
            <Button variant="ghost" size="sm" onClick={() => { goToPage(totalPages); setDialogOpen(false); }} disabled={isLoading || currentPage === totalPages}>Last page</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog> : null}
    </nav>
  );
}
